import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCreateCandidateNote } from '@/hooks/use-candidates';
import { extractErrorMessage } from '@/lib/errors';
import type { CandidateNoteType } from '@/types/candidates';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Paperclip, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EditorToolbar } from '@/components/ui/editor-toolbar';
import { useNoteMentionExtension } from './candidate-note-mention';

const NOTE_TYPE_OPTIONS: Array<{ value: CandidateNoteType; label: string }> = [
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'INTERVIEW_FEEDBACK', label: 'Interview feedback' },
];

interface CandidateNoteComposerProps {
  candidateId: string;
}

export function CandidateNoteComposer({ candidateId }: CandidateNoteComposerProps) {
  const [noteType, setNoteType] = useState<CandidateNoteType>('INTERNAL');
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const createNote = useCreateCandidateNote(candidateId);
  const mention = useNoteMentionExtension();

  const handleSubmit = async (editor: ReturnType<typeof useEditor>) => {
    if (!editor) return;
    const html = editor.getHTML();
    const plain = editor.getText().trim();
    if (!plain) return;
    setError(null);

    const ids = new Set<string>();
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'mention') {
        const id = node.attrs.id as string | null;
        if (id) ids.add(id);
      }
    });

    try {
      await createNote.mutateAsync({
        body: html,
        noteType,
        mentionedEmployeeIds: ids.size > 0 ? Array.from(ids) : undefined,
      });
      editor.commands.clearContent();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to post note'));
    }
  };

  const editor = useEditor({
    extensions: [
      // StarterKit v3 bundles Link; disable it so the configured Link below is
      // the only `link` extension (avoids TipTap's duplicate-extension warning).
      StarterKit.configure({ heading: false, link: false }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: 'text-hygge underline', rel: 'noopener noreferrer' },
      }),
      Placeholder.configure({
        placeholder: 'Write a note… Use @ to mention someone.',
      }),
      mention,
    ],
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none min-h-[72px] w-full bg-transparent px-3 py-2 text-sm focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      setIsEmpty(editor.getText().trim().length === 0);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        void handleSubmit(editor);
      }
    };
    const dom = editor.view.dom;
    dom.addEventListener('keydown', handler);
    return () => dom.removeEventListener('keydown', handler);
  }, [editor, noteType]);

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="overflow-hidden rounded-md border border-input">
        <EditorToolbar editor={editor} />
        <EditorContent editor={editor} />
      </div>
      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Select
            value={noteType}
            onValueChange={(v) => setNoteType(v as CandidateNoteType)}
            disabled={createNote.isPending}
          >
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NOTE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled
                  className="h-8 w-8 text-muted-foreground"
                  aria-label="Attach file (coming soon)"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Attachments ship with Files tab</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span className="text-xs text-muted-foreground">
            Markdown supported · Ctrl+Enter to send ·{' '}
            <span className="font-medium text-hygge">@mention</span> triggers notifications
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => editor && handleSubmit(editor)}
          disabled={createNote.isPending || isEmpty}
        >
          <Send className="mr-1.5 h-3.5 w-3.5" />
          Send
        </Button>
      </div>
    </div>
  );
}
